import { FC } from 'react'
import { KudosDto } from '../../../types'
import { Card } from 'antd'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import PerButtonCircle from '../ButtonCircle'
import PerIcon from '../Icon'
import { useTranslation } from 'react-i18next'
import './Carousel.less'
import { ApolloError } from '@apollo/client'
import { KUDOS_CAROUSEL_PAGE_SIZE } from '../../../graphql/kudos/util'
import AttentionPointCard from '../AttentionPointCard/AttentionPointCard'

export interface AttentionPointCarouselProps {
  attentionPoints: KudosDto[]
  nameDisplayPreference: string
  loading?: boolean
  error: ApolloError | null
  currentPage: number
  totalPages: number
  onNextPage: () => void
  onPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export const AttentionPointCarousel: FC<AttentionPointCarouselProps> = ({
  attentionPoints,
  nameDisplayPreference,
  loading,
  error = undefined,
  currentPage,
  totalPages,
  onNextPage,
  onPreviousPage,
  hasNextPage,
  hasPreviousPage,
}) => {
  const { t } = useTranslation()

  const renderAttentionPointsCards = () => {
    const emptySlots = KUDOS_CAROUSEL_PAGE_SIZE - attentionPoints.length
    const emptyCards = Array.from({ length: emptySlots }, (_, index) => (
      <div key={`empty-${index}`} className="kudos-carousel__empty-slot" />
    ))

    return (
      <div className="kudos-carousel__cards">
        {attentionPoints.map((attentionPointItem) => {
          return (
            <AttentionPointCard
              key={attentionPointItem.id}
              attentionPoint={attentionPointItem}
              nameDisplayPreference={nameDisplayPreference}
            />
          )
        })}
        {emptyCards}
      </div>
    )
  }

  const renderNavigation = () => {
    if (totalPages <= 1) return null

    return (
      <PerFlexRow justify="space-between" align="middle" width="fullWidth">
        {hasPreviousPage ? (
          <PerButtonCircle
            icon="chevronLeft_stroke"
            color="secondary"
            variant="pastel"
            onClick={onPreviousPage}
            tooltip={t('attentionPoint.previousPage')}
          />
        ) : (
          <div style={{ width: '32px', height: '32px' }} />
        )}

        <PerText color="secondary" fontSize="s">
          {currentPage + 1} {t('attentionPoint.of')} {totalPages}
        </PerText>

        {hasNextPage ? (
          <PerButtonCircle
            icon="chevronRight_stroke"
            color="secondary"
            variant="pastel"
            onClick={onNextPage}
            tooltip={t('attentionPoint.nextPage')}
          />
        ) : (
          <div style={{ width: '32px', height: '32px' }} />
        )}
      </PerFlexRow>
    )
  }

  const shouldShowNavigation = () => {
    return totalPages > 1
  }

  const renderStateCard = (content: React.ReactNode, hasFixedHeight = true) => (
    <Card bordered={false} style={hasFixedHeight ? { height: '280px' } : undefined}>
      <PerFlexRow
        direction="column"
        justify="center"
        align="middle"
        width="fullWidth"
        className={hasFixedHeight ? 'fullHeight' : undefined}
      >
        {content}
      </PerFlexRow>
    </Card>
  )

  const renderLoadingState = () =>
    renderStateCard(
      <>
        <PerIcon name="loading_fill" size={32} color="primary" spin />
        <PerText color="secondary" align="center" mt="s">
          {t('attentionPoint.loading')}
        </PerText>
      </>,
      true,
    )

  const renderErrorState = () =>
    renderStateCard(
      <PerText color="error" align="center">
        {t('attentionPoint.error')}
      </PerText>,
      false,
    )

  const renderEmptyState = () =>
    renderStateCard(
      <PerText color="secondary" align="center">
        {t('attentionPoint.noAttentionPoints')}
      </PerText>,
      false,
    )

  if (loading || error || attentionPoints.length === 0) {
    return <>{loading ? renderLoadingState() : error ? renderErrorState() : renderEmptyState()}</>
  }

  return (
    <>
      <Card bordered={false} style={{ height: shouldShowNavigation() ? '280px' : '232px' }}>
        <PerFlexRow direction="column" justify="space-between" className="fullHeight">
          {renderAttentionPointsCards()}
          {shouldShowNavigation() && renderNavigation()}
        </PerFlexRow>
      </Card>
    </>
  )
}

export default AttentionPointCarousel
